/* ═══════════════════════════════════════════════════════════════════
   TOAST · global notification component
   PDP-CART-DRAWER-CANONICAL-ALIGN-1 · Phase 2 of 4
   Canonical reference: 20_CART_BEHAVIOR_SPEC.md §13

   Public API · window.showToast(type, payload)
     · type    · "success" | "error" | "warning" | "info"
     · payload · string (title) OR object { title, detail?, action? }
                 action = { label: string, handler: function }

   Behaviour:
     · Top-right on desktop · top centred banner on mobile
     · Auto-dismiss after 8 s
     · MAX 3 concurrent · 4th displaces oldest
     · ARIA: success/info = role=status aria-live=polite;
             error/warning = role=alert aria-live=assertive
     · Idempotent · safe to call before DOMContentLoaded
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var TOAST_TIMEOUT_MS = 8000;
  var MAX_CONCURRENT = 3;
  var DISMISS_ANIM_MS = 300;
  var activeToasts = [];

  function ensureContainer() {
    var c = document.querySelector('[data-toast-container]');
    if (c) return c;
    c = document.createElement('div');
    c.setAttribute('data-toast-container', '');
    c.className = 'toast-container';
    c.setAttribute('aria-label', 'Notifications');
    (document.body || document.documentElement).appendChild(c);
    return c;
  }

  function escapeHTML(str) {
    if (str == null) return '';
    return String(str).replace(/[&<>"']/g, function (ch) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch];
    });
  }

  function dismissToast(toast) {
    if (!toast || toast.__dismissing) return;
    toast.__dismissing = true;
    var idx = activeToasts.indexOf(toast);
    if (idx >= 0) activeToasts.splice(idx, 1);
    toast.classList.add('toast--dismissing');
    if (toast.__autoDismissTimer) { clearTimeout(toast.__autoDismissTimer); toast.__autoDismissTimer = null; }
    setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, DISMISS_ANIM_MS);
  }

  function buildToast(type, payload) {
    var iconChar = { success: '✓', error: '!', warning: '⚠', info: 'ⓘ' }[type] || 'ⓘ';
    var isAssertive = (type === 'error' || type === 'warning');

    var title = (typeof payload === 'string') ? payload : (payload && payload.title) || '';
    var detail = (typeof payload === 'object' && payload && payload.detail) || '';
    var action = (typeof payload === 'object' && payload && payload.action) || null;

    var toast = document.createElement('div');
    toast.className = 'toast toast--' + type;
    toast.setAttribute('role', isAssertive ? 'alert' : 'status');
    toast.setAttribute('aria-live', isAssertive ? 'assertive' : 'polite');

    var html = '';
    html += '<span class="toast__icon" aria-hidden="true">' + escapeHTML(iconChar) + '</span>';
    html += '<div class="toast__body">';
    html += '<p class="toast__title">' + escapeHTML(title) + '</p>';
    if (detail) html += '<p class="toast__detail">' + escapeHTML(detail) + '</p>';
    html += '</div>';
    if (action && action.label) {
      html += '<button type="button" class="toast__action">' + escapeHTML(action.label) + '</button>';
    }
    html += '<button type="button" class="toast__dismiss" aria-label="Dismiss notification">&times;</button>';
    toast.innerHTML = html;

    if (action && typeof action.handler === 'function') {
      var actionBtn = toast.querySelector('.toast__action');
      if (actionBtn) {
        actionBtn.addEventListener('click', function () {
          try { action.handler(); }
          catch (e) { console.error('[toast] action handler error:', e); }
          dismissToast(toast);
        });
      }
    }
    var dismissBtn = toast.querySelector('.toast__dismiss');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () { dismissToast(toast); });
    }

    return toast;
  }

  window.showToast = function (type, payload) {
    if (!type || !payload) return null;
    var container = ensureContainer();
    var toast = buildToast(type, payload);
    container.appendChild(toast);
    activeToasts.push(toast);

    // Bump oldest if exceeded
    while (activeToasts.length > MAX_CONCURRENT) {
      dismissToast(activeToasts[0]);
    }

    toast.__autoDismissTimer = setTimeout(function () { dismissToast(toast); }, TOAST_TIMEOUT_MS);

    return toast;
  };

  // Convenience: dismiss all toasts on page unload to clear stale state on SPA-like navigations.
  window.addEventListener('beforeunload', function () {
    activeToasts.slice().forEach(dismissToast);
  });
})();
